import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView, LogBox } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import { scaleOrdinal } from 'd3-scale';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { Loader } from '../../Common/Loader';
import { colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import { ISitee } from '../../ClientLanding';
import { useNav } from '../../../providers/NavProvider';

interface Props {
  siteId: string;
  logTitle: string;
  sitesResp: ISitee;
  projectId: string;
}

export default function PM10Monitoring(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);
  const [pm10monitoringLogData, setPM10monitoringLogData] = useState<null | any>(null);
  const [pm10monitoringLogColumns, setPM10monitoringLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const logTitle = props.logTitle;
          const accessToken = await getAccessToken();
          const json = await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId, props.projectId, packageSite.id);
          setPM10monitoringLogData(json.items);
          setPM10monitoringLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return pm10monitoringLogData && pm10monitoringLogColumns
    ? <CreateLogCharts pm10monitoringLogData={pm10monitoringLogData} pm10monitoringLogColumns={pm10monitoringLogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateLogChartsProps {
  pm10monitoringLogData: any;
  pm10monitoringLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

const CreateLogCharts: React.FC<CreateLogChartsProps> = ({ pm10monitoringLogData, pm10monitoringLogColumns, siteId, logTitle, clientAPIURL }) => {
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = pm10monitoringLogColumns && [
    ...pm10monitoringLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const [filteredData, setFilteredData] = useState<any[]>(pm10monitoringLogData ?? []);

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const newItem = { ...result.items[0] };
        setFilteredData(prev => {
          const without = prev.filter((oItem: any) => oItem.ID !== item.ID);
          return [...without, newItem];
        });
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  const chartData = useMemo(() => {
    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))] as string[];

    const colorLine = scaleOrdinal(schemeCategory10);
    const makeDatasets = (series: number[][]) =>
      series.map((values, index) => ({
        data: values,
        strokeWidth: 2,
        color: (opacity = 1) => colorLine(String(index)) + Math.round(opacity * 255).toString(16),
      }));

    // Conformity / Non Conformity counts per period
    const confPerPeriod = uniquePeriod.map(p => filteredData.filter((i: any) => i.Period === p && i.Conformity === 'Conformity').length);
    const nonConfPerPeriod = uniquePeriod.map(p => filteredData.filter((i: any) => i.Period === p && i.Conformity === 'Non Conformity').length);

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineData1 = { labels: uniquePeriod, datasets: makeDatasets([confPerPeriod, nonConfPerPeriod]) };

    // Max MonitoringResultsugm3 per LocationRef per period
    const validItems = filteredData.filter((i: any) => i.MonitoringResultsugm3 !== null && i.MonitoringResultsugm3 !== '');
    const isValidNumber = (v: any) => typeof v === 'number' && !isNaN(v) && isFinite(v);
    const maxByLoc = (loc: string) => uniquePeriod.map(p => {
      const items = validItems.filter((i: any) => i.Period === p && i.LocationRef === loc);
      return items.length > 0 ? Math.max(...items.map((i: any) => isValidNumber(i.MonitoringResultsugm3) ? i.MonitoringResultsugm3 : 0)) : 0;
    });

    const lineData2 = {
      labels: uniquePeriod,
      datasets: makeDatasets([
        maxByLoc('AYC'),
        maxByLoc('DGDA'),
        maxByLoc('NORTH'),
        maxByLoc('SOUTH'),
        uniquePeriod.map(() => 340),  // compliance limit
      ]),
    };

    // PieChartComp contract: data[].name=string (non-null, non-empty), data[].value=number, data[].color=string
    const pieAcc: Record<string, number> = {};
    filteredData.forEach((i: any) => { if (i.Conformity) pieAcc[i.Conformity] = (pieAcc[i.Conformity] ?? 0) + 1; });
    const byConformityPieData = Object.entries(pieAcc).map(([name, value], idx) => ({
      name, value, color: colorType5(idx.toString()),
    }));

    return { uniquePeriod, lineData1, lineData2, byConformityPieData };
  }, [filteredData]);

  const legendData1 = [{ name: 'Conformity' }, { name: 'Non Conformity' }];
  const legendData2 = [{ name: 'AYC' }, { name: 'DGDA' }, { name: 'North' }, { name: 'South' }, { name: 'Compliance' }];

  const exceedanceDateSet = new Set(
    filteredData.filter((i: any) => i.Conformity === 'Non Conformity' && i.Date).map((i: any) => i.Date)
  );
  const allDateSet = new Set(filteredData.filter((i: any) => i.Date).map((i: any) => i.Date));
  const exceedanceDays = exceedanceDateSet.size;
  const nonExceedanceDays = [...allDateSet].filter(d => !exceedanceDateSet.has(d)).length;
  const exceedanceEntries = filteredData.filter((i: any) => i.Conformity === 'Non Conformity').length;
  const nonExceedanceEntries = filteredData.filter((i: any) => i.Conformity === 'Conformity').length;

  const kpiItems = [
    { label: 'Total', value: allDateSet.size, unit: 'days', subtitle: `${filteredData.length} entries`, color: '#1565C0' },
    { label: 'Non Exceedances', value: nonExceedanceDays, unit: 'days', subtitle: `${nonExceedanceEntries} entries`, color: '#2E7D32' },
    { label: 'Exceedances', value: exceedanceDays, unit: 'days', subtitle: `${exceedanceEntries} entries`, color: '#C62828' },
  ];

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab && defaultTab === 'list' ? 1 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={{ zIndex: 200 }}>
                <LogKpiRow items={kpiItems}>
                  <LogDateRangeFilter data={pm10monitoringLogData} onFilterChange={setFilteredData} />
                </LogKpiRow>
              </View>
              <View>
                <View style={logStyles.container}>
                  {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byConformityPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative)'} ChartTitle={"PM10 Results"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendData1} data={chartData.lineData1} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"PM10 Results"} />
                  </View>
                </View>
              </View>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardTwo}>
                    <LineChartScreen legend={legendData2} data={chartData.lineData2} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"PM10 Levels µg/m3"} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {filteredData && ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        funcSetShowFilter={() => {}}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
